import { ChevronLeft, ChevronRight, Copy, Eye, EyeOff, Plus, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import type { APIClient } from "./api";
import type { AdminCodeBatch, AdminCodeRequest, AdminGeneratedCodes } from "./types";

const LABELS = { UNIQUE: "独立单次码", SHARED: "共享限量码", PER_USER: "每人一次码" };
const REWARDS = { wood: "木材", ore: "矿石", gold: "金币", diamond: "钻石" };

export function AdminCodes({ api }: { api: APIClient }) {
  const [items, setItems] = useState<AdminCodeBatch[]>([]);
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [shown, setShown] = useState<AdminGeneratedCodes | null>(null);
  const [name, setName] = useState("");
  const [type, setType] = useState<AdminCodeRequest["type"]>("UNIQUE");
  const [count, setCount] = useState(1);
  const [maxUses, setMaxUses] = useState(100);
  const [reward, setReward] = useState({ wood: 0, ore: 0, gold: 0, diamond: 0 });
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const pending = useRef<{ signature: string; id: string } | null>(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await api.adminCodeBatches(offset));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "读取失败");
    } finally {
      setLoading(false);
    }
  }, [api, offset]);
  useEffect(() => {
    void load();
  }, [load]);
  const generate = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const payload: AdminCodeRequest = {
      name: name.trim(),
      type,
      count: type === "UNIQUE" ? count : 1,
      maxUses: type === "UNIQUE" ? 1 : type === "SHARED" ? maxUses : 0,
      reward,
      validFrom: validFrom ? new Date(validFrom).getTime() : 0,
      validTo: validTo ? new Date(validTo).getTime() : 0,
    };
    if (
      !payload.name ||
      !Object.values(reward).some((value) => value > 0) ||
      (payload.validTo && payload.validTo <= payload.validFrom)
    ) {
      setMessage("请填写批次名称、奖励和有效时间窗");
      return;
    }
    if (
      !window.confirm(
        `创建「${payload.name}」${payload.count} 个${LABELS[type]}，确认奖励和领取次数？`,
      )
    )
      return;
    const signature = JSON.stringify(payload);
    if (pending.current?.signature !== signature) {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      pending.current = {
        signature,
        id: `op_codes_${Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("")}`,
      };
    }
    setBusy(true);
    setMessage("");
    try {
      setShown(await api.adminCreateCodes(payload, pending.current.id));
      pending.current = null;
      setMessage("批次已创建");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "创建失败");
    } finally {
      setBusy(false);
    }
  };
  const reveal = async (id: string) => {
    setBusy(true);
    setShown(null);
    setMessage("");
    try {
      setShown(await api.adminRevealCodes(id));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "展示失败");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="admin-codes">
      <form className="admin-code-form" onSubmit={(event) => void generate(event)}>
        <h3>创建激活码</h3>
        <label>
          批次名称
          <input
            required
            maxLength={80}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label>
          码型
          <select value={type} onChange={(event) => setType(event.target.value as typeof type)}>
            {Object.entries(LABELS).map(([id, label]) => (
              <option value={id} key={id}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {type === "UNIQUE" && (
          <label>
            数量
            <input
              type="number"
              min={1}
              max={100}
              required
              value={count}
              onChange={(event) => setCount(Number(event.target.value))}
            />
          </label>
        )}
        {type === "SHARED" && (
          <label>
            总领取次数
            <input
              type="number"
              min={1}
              max={10000}
              required
              value={maxUses}
              onChange={(event) => setMaxUses(Number(event.target.value))}
            />
          </label>
        )}
        <fieldset>
          <legend>每次领取奖励</legend>
          {Object.entries(REWARDS).map(([id, label]) => (
            <label key={id}>
              {label}
              <input
                type="number"
                min={0}
                max={1000000}
                required
                value={reward[id as keyof typeof reward]}
                onChange={(event) =>
                  setReward((current) => ({ ...current, [id]: Number(event.target.value) }))
                }
              />
            </label>
          ))}
        </fieldset>
        <label>
          生效时间
          <input
            type="datetime-local"
            value={validFrom}
            onChange={(event) => setValidFrom(event.target.value)}
          />
        </label>
        <label>
          到期时间
          <input
            type="datetime-local"
            value={validTo}
            onChange={(event) => setValidTo(event.target.value)}
          />
        </label>
        <button className="primary-command" disabled={busy} type="submit">
          <Plus size={16} />
          创建
        </button>
      </form>
      <section className="admin-code-list">
        <header>
          <h3>激活码批次</h3>
          <button
            className="icon-button"
            title="刷新"
            aria-label="刷新激活码批次"
            disabled={loading || busy}
            onClick={() => void load()}
          >
            <RefreshCw size={16} />
          </button>
        </header>
        {message && <p role="status">{message}</p>}
        {shown && (
          <section className="admin-code-reveal">
            <header>
              <strong>激活码</strong>
              <button
                className="icon-button"
                title="隐藏激活码"
                aria-label="隐藏激活码"
                onClick={() => setShown(null)}
              >
                <EyeOff size={16} />
              </button>
              <button
                className="icon-button"
                title="复制激活码"
                aria-label="复制激活码"
                onClick={() => {
                  if (!navigator.clipboard) {
                    setMessage("可选中下方文本复制");
                    return;
                  }
                  void navigator.clipboard
                    .writeText(shown.codes.join("\n"))
                    .then(() => setMessage("已复制"))
                    .catch(() => setMessage("无法复制，可选中下方文本"));
                }}
              >
                <Copy size={16} />
              </button>
            </header>
            <textarea
              readOnly
              value={shown.codes.join("\n")}
              aria-label="激活码明文"
              onFocus={(event) => event.currentTarget.select()}
            />
          </section>
        )}
        {loading ? (
          <p>读取中</p>
        ) : items.length === 0 ? (
          <p>暂无批次</p>
        ) : (
          items.map((item) => (
            <article className="admin-code-row" key={item.id}>
              <div>
                <strong>{item.name}</strong>
                <small>
                  {LABELS[item.type]} · {item.count} 个 · 已领 {item.uses}
                  {item.type === "SHARED" ? ` / ${item.maxUses}` : ""}
                </small>
                <small>
                  {Object.entries(item.reward)
                    .filter(([, value]) => value > 0)
                    .map(([id, value]) => `${REWARDS[id as keyof typeof REWARDS]} ${value}`)
                    .join(" · ")}
                </small>
                <small>
                  {item.validTo ? `到期 ${new Date(item.validTo).toLocaleString()}` : "长期有效"} ·{" "}
                  {item.status === "active" ? "启用" : "停用"}
                </small>
              </div>
              <button
                className="icon-button"
                disabled={busy || !item.canReveal}
                title={item.canReveal ? "显示激活码" : "旧 CLI 码不保存明文"}
                aria-label={`显示${item.name}激活码`}
                onClick={() => void reveal(item.id)}
              >
                <Eye size={16} />
              </button>
            </article>
          ))
        )}
        <footer>
          <button
            className="icon-button"
            title="上一页"
            aria-label="上一页激活码"
            disabled={loading || offset === 0}
            onClick={() => setOffset(Math.max(0, offset - 20))}
          >
            <ChevronLeft size={16} />
          </button>
          <span>{offset / 20 + 1}</span>
          <button
            className="icon-button"
            title="下一页"
            aria-label="下一页激活码"
            disabled={loading || items.length < 20}
            onClick={() => setOffset(offset + 20)}
          >
            <ChevronRight size={16} />
          </button>
        </footer>
      </section>
    </div>
  );
}
